<template>
  <div :class="$style.timetable">
    <div :class="$style.timetable__headers">
      <div
        v-for="(c, idx) in columns"
        :key="`header-${title(c.date)}`"
        :class="[$style.headers__column, $style.header]"
      >
        <h2 :class="titleStyle(c.date)">{{ title(c.date) }}</h2>
        <ButtonIconOnly
          v-if="idx === 0"
          :class="$style.header__prev"
          @click="$emit('onPrevClicked')"
          :disabled="!hasPrev"
        >
          <template #icon>
            <IconChevron direction="left" />
          </template>
        </ButtonIconOnly>
        <ButtonIconOnly
          :class="$style.header__next"
          @click="$emit('onNextClicked')"
          v-if="idx === columns.length - 1"
        >
          <template #icon>
            <IconChevron direction="right" />
          </template>
        </ButtonIconOnly>
      </div>
    </div>

    <div :class="$style.timetable__body">
      <div
        v-for="c in columns"
        :key="`body-${title(c.date)}`"
        :class="[$style.body__column, $style.column]"
      >
        <ol :class="$style.column__slots">
          <li
            v-for="ts in c.availableTimeSlots"
            :key="`${ts.startsAt}::${ts.endsAt}`"
          >
            <TimeSlotCard
              :timeSlot="ts"
              @click="$emit('update:modelValue', ts)"
            />
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import TimeSlotCard from './TimeSlotCard.vue'
import IconChevron from '@/components/icons/IconChevron.vue'

import { ja } from 'date-fns/locale'
import { useCssModule, ref, onMounted } from 'vue'
import type { TimeSlot } from './useTimeSlot.ts'
import { identity, uniq } from 'lodash-es'
import * as kenall from '@/hooks/useKenAll'

import { format } from 'date-fns'

export type Column = {
  date: Date
  availableTimeSlots: TimeSlot[]
}
const props = defineProps<{
  columns: Column[]
  hasPrev: boolean
}>()

defineEmits<{
  (e: 'update:modelValue', value: TimeSlot): void
  (e: 'onPrevClicked'): void
  (e: 'onNextClicked'): void
}>()

const title = (d: Date) => format(d, 'y/M/d (E)', { locale: ja })
const style = useCssModule()

const holidays = ref<string[]>([])
const isHoliday = (d: Date) => holidays.value.includes(format(d, 'yyyy-MM-dd'))

const titleStyle = (d: Date) => [
  style.header__title,
  {
    [style['header__title--holiday']]: isHoliday(d),
    [style['header__title--sunday']]: !isHoliday(d) && d.getDay() === 0,
    [style['header__title--saturday']]: !isHoliday(d) && d.getDay() === 6,
  },
]

onMounted(async () => {
  const years = uniq(props.columns.map(c => c.date.getFullYear()))
  holidays.value = (
    await Promise.all(
      years.map(async y => {
        const holidays = await kenall.getHolidaysYear(y)
        return holidays.data.map(d => d.date)
      })
    )
  ).flatMap(identity<string[]>)
})
</script>

<style module lang="scss">
.timetable {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__headers {
    display: flex;
    flex-shrink: 0;
  }

  &__body {
    display: flex;
    height: 500px;
    overflow-y: auto;
  }
}

.headers__column {
  flex: 1;
  padding: 8px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.body__column {
  flex: 1;

  &:nth-child(n + 1) {
    border-left: 1px solid $color-border-low-emphasis;
  }
}

.column {
  &__slots {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}

.header {
  position: relative;

  h2 {
    width: 100%;
    padding: 4px;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
  }

  &__title {
    &--sunday,
    &--holiday {
      color: $color-label-red;
    }

    &--saturday {
      color: $color-label-blue;
    }
  }

  &__prev {
    font-size: 24px;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  &__next {
    font-size: 24px;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
